import * as THREE from "three";
import type { WeaponDef } from "../data/arsenal";
import { buildGunMesh } from "./gunmesh";

// smoothstep — module-level so applyBigSwing doesn't re-create the closure every call
const ss = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * First-person weapon viewmodel, rendered in its OWN scene with a separate camera, drawn AFTER the
 * world with a cleared depth buffer so the gun is always on top and correctly self-sorted. Because it
 * lives in its own scene/camera it is SCALE-INDEPENDENT — ported verbatim from zombies (its little
 * 0.3-unit rest pose is correct regardless of longfight's stud world). Procedural box gun; recoil +
 * muzzle flash + Pack-a-Punch glow + dual-wield (PaP 5+) all work.
 */
export class ViewModel {
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private gunGroup = new THREE.Group();
  private muzzle: THREE.Mesh;
  private muzzleLight: THREE.PointLight;
  private muzzleL: THREE.Mesh; // left gun muzzle (dual-wield, PaP 5+)
  private muzzleLightL: THREE.PointLight;
  private dual = false;
  private recoil = 0;
  private swing = 0;
  private restPos = new THREE.Vector3(0.32, -0.28, -0.6);
  private papLevel = 0;
  private glowMats: THREE.MeshLambertMaterial[] = [];
  private glowColor = new THREE.Color();
  private t = 0;
  private reloading = false;
  private reloadAmt = 0;
  private googlyPupils: THREE.Mesh[] = [];
  private eyeOff = { x: 0, y: 0 };
  private eyeVel = { x: 0, y: 0 };
  private motion = { vx: 0, vz: 0, vy: 0 };

  setReloading(on: boolean): void {
    this.reloading = on;
  }

  /** Match the world camera's FOV so the gun's perspective is consistent. */
  setFov(fov: number): void {
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  setHeldMotion(vx: number, vz: number, vy: number): void {
    this.motion.vx = vx;
    this.motion.vz = vz;
    this.motion.vy = vy;
  }

  constructor(mainCamera: THREE.PerspectiveCamera) {
    this.camera = new THREE.PerspectiveCamera(mainCamera.fov, mainCamera.aspect, 0.01, 10);
    this.scene.add(this.gunGroup);
    this.gunGroup.position.copy(this.restPos);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(0.5, 1, 1);
    this.scene.add(key);

    this.muzzle = new THREE.Mesh(
      new THREE.PlaneGeometry(0.25, 0.25),
      new THREE.MeshBasicMaterial({ color: 0xffdd66, transparent: true, opacity: 0 }),
    );
    this.gunGroup.add(this.muzzle);
    this.muzzleLight = new THREE.PointLight(0xffcc66, 0, 4);
    this.gunGroup.add(this.muzzleLight);
    this.muzzleL = new THREE.Mesh(
      new THREE.PlaneGeometry(0.25, 0.25),
      new THREE.MeshBasicMaterial({ color: 0xffdd66, transparent: true, opacity: 0 }),
    );
    this.muzzleL.visible = false;
    this.gunGroup.add(this.muzzleL);
    this.muzzleLightL = new THREE.PointLight(0xffcc66, 0, 4);
    this.gunGroup.add(this.muzzleLightL);

    window.addEventListener("resize", () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
    });
  }

  setWeapon(def: WeaponDef, papLevel = 0): void {
    const keep = new Set<THREE.Object3D>([this.muzzle, this.muzzleLight, this.muzzleL, this.muzzleLightL]);
    for (const child of [...this.gunGroup.children]) {
      if (keep.has(child)) continue;
      this.gunGroup.remove(child);
      child.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material;
        for (const m of Array.isArray(mat) ? mat : mat ? [mat] : []) m.dispose();
      });
    }
    this.papLevel = papLevel;
    this.dual = papLevel >= 5;
    this.glowMats = [];
    this.googlyPupils = [];
    if (def.melee) {
      const main = buildBriefcaseMesh(def.viewColor);
      this.glowMats.push(...main.mats);
      this.gunGroup.add(main.group);
      if (papLevel >= 5) {
        const left = buildBriefcaseMesh(def.viewColor);
        left.group.position.x = -0.64;
        left.group.rotation.y *= -1;
        this.glowMats.push(...left.mats);
        this.gunGroup.add(left.group);
      }
    } else if (def.deploy) {
      const main = buildChickenSphereMesh(def.viewColor);
      this.glowMats.push(...main.mats);
      this.googlyPupils.push(...main.pupils);
      this.gunGroup.add(main.group);
      if (papLevel >= 5) {
        const left = buildChickenSphereMesh(def.viewColor);
        left.group.position.x = -0.5;
        this.glowMats.push(...left.mats);
        this.googlyPupils.push(...left.pupils);
        this.gunGroup.add(left.group);
      }
    } else {
      const main = this.buildProceduralGun(def.viewColor);
      main.position.set(0, 0, 0);
      this.gunGroup.add(main);
      if (papLevel >= 5) {
        const left = this.buildProceduralGun(def.viewColor);
        left.position.set(-0.64, 0, 0);
        this.gunGroup.add(left);
      }
    }
    this.muzzle.position.set(0, 0.02, -0.55);
    this.muzzleLight.position.set(0, 0.02, -0.55);
    this.muzzleL.position.set(-0.64, 0.02, -0.55);
    this.muzzleLightL.position.set(-0.64, 0.02, -0.55);
    this.muzzleL.visible = this.dual;
  }

  private buildProceduralGun(color: number): THREE.Group {
    const { group, mats } = buildGunMesh(color);
    this.glowMats.push(...mats);
    return group;
  }

  private throwT = 0;
  throwAnim(): void {
    this.throwT = 1;
  }

  fire(): void {
    this.recoil = 1;
    (this.muzzle.material as THREE.MeshBasicMaterial).opacity = 1;
    this.muzzle.rotation.z = Math.random() * Math.PI;
    this.muzzleLight.intensity = 2.5;
    if (this.dual) {
      (this.muzzleL.material as THREE.MeshBasicMaterial).opacity = 1;
      this.muzzleL.rotation.z = Math.random() * Math.PI;
      this.muzzleLightL.intensity = 2.5;
    }
  }

  melee(): void {
    this.swing = 1;
  }

  private bigSwing = 0;
  private swingType = 0;

  meleeSmash(): void {
    this.bigSwing = 1;
    this.swingType = (Math.random() * 3) | 0;
  }

  private applyBigSwing(): void {
    const p = 1 - this.bigSwing;
    let dx = 0, dy = 0, dz = 0, rx = 0, ry = 0, rz = 0;
    if (this.swingType === 0) {
      const raise = ss(0, 0.3, p);
      const strike = ss(0.3, 0.64, p);
      dx = raise * 0.14 - strike * 0.2;
      dy = raise * 0.6 - strike * 0.95;
      dz = raise * 0.2 - strike * 0.34;
      rx = -raise * 0.85 + strike * 2.2;
      ry = raise * 0.2 - strike * 0.2;
      rz = raise * 0.1 - strike * 0.15;
    } else if (this.swingType === 1) {
      const pull = ss(0, 0.3, p);
      const sweep = ss(0.3, 0.68, p);
      dx = pull * 0.45 - sweep * 1.2;
      dy = Math.sin(p * Math.PI) * 0.16;
      dz = pull * 0.1 - sweep * 0.24;
      rx = sweep * 0.5;
      ry = -pull * 0.55 + sweep * 1.2;
      rz = pull * 0.65 - sweep * 2.0;
    } else {
      const dip = ss(0, 0.3, p);
      const up = ss(0.3, 0.66, p);
      dx = -up * 0.14;
      dy = -dip * 0.5 + up * 1.05;
      dz = dip * 0.16 - up * 0.36;
      rx = dip * 0.6 - up * 1.9;
      ry = up * 0.3;
      rz = -up * 0.35;
    }
    const amp = 1 - ss(0.74, 1, p);
    const R = this.restPos;
    this.gunGroup.position.set(R.x + dx * amp, R.y + dy * amp, R.z + dz * amp);
    this.gunGroup.rotation.set(rx * amp, ry * amp, rz * amp);
  }

  update(dt: number): void {
    this.t += dt;
    this.recoil = Math.max(0, this.recoil - dt * 6);
    this.swing = Math.max(0, this.swing - dt * 4);
    this.bigSwing = Math.max(0, this.bigSwing - dt * 2.5);
    this.throwT = Math.max(0, this.throwT - dt * 3.2);
    this.reloadAmt += ((this.reloading ? 1 : 0) - this.reloadAmt) * Math.min(1, dt * 9);
    if (this.bigSwing > 0) {
      this.applyBigSwing();
    } else {
      // idle sway/recoil pose — only needed on this branch (the bigSwing branch poses fully itself)
      const kick = this.recoil * this.recoil;
      const sw = Math.sin(this.swing * Math.PI);
      const th = Math.sin(this.throwT * Math.PI);
      const rl = this.reloadAmt;
      const rlRock = Math.sin(this.t * 16) * 0.25 * rl;
      this.gunGroup.position.set(
        this.restPos.x - sw * 0.25 + th * 0.14,
        this.restPos.y + kick * 0.04 - sw * 0.08 - rl * 0.18 - th * 0.2,
        this.restPos.z + kick * 0.12 + th * 0.08,
      );
      this.gunGroup.rotation.x = kick * 0.25 + sw * 0.4 + rl * 0.7 + th * 0.25;
      this.gunGroup.rotation.z = -sw * 0.7 + rlRock - th * 0.55;
    }

    const mm = this.muzzle.material as THREE.MeshBasicMaterial;
    if (mm.opacity > 0) mm.opacity = Math.max(0, mm.opacity - dt * 12);
    if (this.muzzleLight.intensity > 0) this.muzzleLight.intensity = Math.max(0, this.muzzleLight.intensity - dt * 18);
    const ml = this.muzzleL.material as THREE.MeshBasicMaterial;
    if (ml.opacity > 0) ml.opacity = Math.max(0, ml.opacity - dt * 12);
    if (this.muzzleLightL.intensity > 0) this.muzzleLightL.intensity = Math.max(0, this.muzzleLightL.intensity - dt * 18);

    if (this.papLevel > 0 && this.glowMats.length) {
      const lvl = this.papLevel;
      const pulse = 0.35 + 0.4 * Math.sin(this.t * (3 + lvl)) + lvl * 0.12;
      if (lvl >= 4) this.glowColor.setHSL((this.t * (0.15 + lvl * 0.04)) % 1, 1, 0.55);
      else this.glowColor.setHSL(0.62, 1, 0.5);
      for (const m of this.glowMats) {
        m.emissive.copy(this.glowColor);
        m.emissiveIntensity = Math.max(0, pulse);
      }
    }

    if (this.googlyPupils.length) {
      const k = 0.0019;
      const maxO = 0.028;
      const clamp = (v: number): number => Math.max(-maxO, Math.min(maxO, v));
      const tx = clamp(-this.motion.vx * k);
      const ty = clamp(this.motion.vy * 0.6 * k - Math.hypot(this.motion.vx, this.motion.vz) * k * 0.22);
      const stiff = 150;
      const damp = 13;
      this.eyeVel.x += ((tx - this.eyeOff.x) * stiff - this.eyeVel.x * damp) * dt;
      this.eyeVel.y += ((ty - this.eyeOff.y) * stiff - this.eyeVel.y * damp) * dt;
      this.eyeOff.x += this.eyeVel.x * dt;
      this.eyeOff.y += this.eyeVel.y * dt;
      for (const p of this.googlyPupils) {
        p.position.x = this.eyeOff.x;
        p.position.y = this.eyeOff.y;
      }
    }
  }

  /** Draw the viewmodel on top of the already-rendered world. */
  render(renderer: THREE.WebGLRenderer): void {
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(this.scene, this.camera);
    renderer.autoClear = prevAuto;
  }
}

function buildBriefcaseMesh(color: number): { group: THREE.Group; mats: THREE.MeshLambertMaterial[] } {
  const g = new THREE.Group();
  const leather = new THREE.MeshLambertMaterial({ color });
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.34, 0.12), leather);
  g.add(body);
  const brass = new THREE.MeshLambertMaterial({ color: 0xcaa54a, emissive: 0x3a2c08 });
  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.018, 8, 16, Math.PI), brass);
  handle.rotation.x = Math.PI / 2;
  handle.position.set(0, 0.2, 0);
  g.add(handle);
  for (const x of [-0.14, 0.14]) {
    const latch = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.05, 0.02), brass);
    latch.position.set(x, 0.12, 0.07);
    g.add(latch);
  }
  const pearl = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 8, 8),
    new THREE.MeshLambertMaterial({ color: 0xeae6d6, emissive: 0x222018 }),
  );
  pearl.position.set(0.08, 0.02, 0.07);
  g.add(pearl);
  g.rotation.set(0.15, 0.3, 0);
  g.position.set(0, -0.02, 0);
  return { group: g, mats: [leather, brass] };
}

function buildChickenSphereMesh(color: number): {
  group: THREE.Group;
  mats: THREE.MeshLambertMaterial[];
  pupils: THREE.Mesh[];
} {
  const g = new THREE.Group();
  const R = 0.2;
  const feather = new THREE.MeshLambertMaterial({ color });
  const body = new THREE.Mesh(new THREE.SphereGeometry(R * 0.6, 12, 10), feather);
  g.add(body);
  const comb = new THREE.Mesh(new THREE.SphereGeometry(R * 0.16, 6, 6), new THREE.MeshLambertMaterial({ color: 0xcc2222 }));
  comb.position.set(0, R * 0.55, R * 0.18);
  g.add(comb);
  const beakMat = new THREE.MeshLambertMaterial({ color: 0xe2a300 });
  const beak = new THREE.Mesh(new THREE.ConeGeometry(R * 0.12, R * 0.24, 6), beakMat);
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, R * 0.2, R * 0.5);
  g.add(beak);
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const pupilMat = new THREE.MeshBasicMaterial({ color: 0x111111 });
  const eyeGeom = new THREE.SphereGeometry(R * 0.22, 10, 8);
  const pupilGeom = new THREE.SphereGeometry(R * 0.1, 8, 6);
  const pupils: THREE.Mesh[] = [];
  for (const ex of [-R * 0.26, R * 0.26]) {
    const eye = new THREE.Mesh(eyeGeom, eyeMat);
    eye.position.set(ex, R * 0.3, R * 0.4);
    g.add(eye);
    const pupil = new THREE.Mesh(pupilGeom, pupilMat);
    pupil.position.set(0, 0, R * 0.15);
    eye.add(pupil);
    pupils.push(pupil);
  }
  const cage = new THREE.Mesh(
    new THREE.SphereGeometry(R, 14, 9),
    new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.85 }),
  );
  g.add(cage);
  g.rotation.set(0.1, -0.2, 0);
  g.position.set(0, -0.04, 0);
  return { group: g, mats: [feather, beakMat], pupils };
}
